// src/lib/teaser-lead-flag.ts
//
// Localstorage flag indicating "this anonymous user already submitted
// their email for a particular diagnostic teaser". Drives the
// post-submit UI on /try/results (green check vs amber delayed
// message) and stops the card from re-prompting on reload.
//
// Two failure modes the design has to defend against:
//
// 1. Same-score retakes. The flag is keyed by `estLow-estHigh`, so a
//    user who retakes the diagnostic and happens to land in the same
//    score band (very common — most users improve gradually) would
//    see a stale "delayed" message instead of being asked for their
//    email. Fix: clearAllLeadFlags() is called whenever a fresh
//    diagnostic completes, so each attempt starts clean.
//
// 2. Permanent stale state. If the user submitted weeks ago and is
//    bouncing back to /try/results, the localStorage entry could
//    advertise a "delayed" send that's long since resolved (or
//    timed out). Fix: each entry carries a timestamp; reads older
//    than the TTL are ignored.
//
// Together these mean: "for a given attempt, we remember the outcome
// for a few days; on a new attempt we forget completely."

/** Outcome stored after a successful POST to /api/teaser/lead. */
export type LeadSubmitOutcome = 'sent' | 'delayed'

/**
 * Flags older than this are ignored on read. 7 days is a soft upper
 * bound — long enough that a user revisiting /try/results within a
 * week sees a coherent state, short enough that nothing leaks
 * forever in browsers that hold localStorage indefinitely.
 */
const FLAG_TTL_MS = 7 * 24 * 60 * 60 * 1000

/** All keys we write live under this prefix. */
const FLAG_KEY_PREFIX = 'teaserLeadSubmitted:'

/**
 * Minimal Storage interface so unit tests don't need jsdom. Window's
 * localStorage satisfies this shape; tests pass an in-memory stub.
 */
export type FlagStorage = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
  // For clearAll — Storage has a `length` + indexed accessor, but
  // jsdom doesn't expose those nicely. We instead require a `keys()`
  // method that returns the snapshot of keys to walk. Window's
  // localStorage doesn't have this natively — getKeys() below
  // implements it via Object.keys().
}

function buildKey(estLow: number, estHigh: number): string {
  return `${FLAG_KEY_PREFIX}${estLow}-${estHigh}`
}

type FlagPayload = {
  outcome: LeadSubmitOutcome
  at: number // unix ms
}

/**
 * Parse a stored value into a typed payload. Tolerates three formats
 * for forward / backward compatibility:
 *  - JSON object with outcome + at  (current)
 *  - bare string "sent" / "delayed"  (pre-TTL: from #402)
 *  - "1"                              (legacy pre-truthful-UI: #398)
 * The two legacy formats coerce to "delayed" with no timestamp —
 * which means they're treated as stale by TTL and dropped on read.
 * That's the right behavior: if we don't know when it was written,
 * we don't trust it.
 */
function parseFlag(raw: string | null): FlagPayload | null {
  if (raw === null) return null
  // Current format: JSON.
  if (raw.startsWith('{')) {
    try {
      const obj = JSON.parse(raw) as Partial<FlagPayload>
      if ((obj.outcome === 'sent' || obj.outcome === 'delayed') && typeof obj.at === 'number') {
        return { outcome: obj.outcome, at: obj.at }
      }
    } catch {
      return null
    }
    return null
  }
  // Legacy bare-string formats. Treat as undated → stale.
  if (raw === 'sent' || raw === 'delayed') {
    return { outcome: raw, at: 0 }
  }
  if (raw === '1') {
    return { outcome: 'delayed', at: 0 }
  }
  return null
}

/**
 * Read the lead-submit outcome for a given score band. Returns null
 * when there's no flag, the flag is malformed, or the flag is older
 * than the TTL (treated as cleared).
 *
 * `nowMs` is injectable for tests.
 */
export function readLeadFlag(
  storage: FlagStorage,
  estLow: number,
  estHigh: number,
  nowMs: number = Date.now(),
): LeadSubmitOutcome | null {
  const parsed = parseFlag(storage.getItem(buildKey(estLow, estHigh)))
  if (!parsed) return null
  // Drop in two cases:
  //  - at === 0: undated legacy format (pre-#402). We don't know
  //    when the user actually submitted, so we don't trust it.
  //  - age > TTL: the flag is stale by policy.
  if (parsed.at === 0) return null
  if (nowMs - parsed.at > FLAG_TTL_MS) return null
  return parsed.outcome
}

/** Write the flag with the current timestamp. */
export function writeLeadFlag(
  storage: FlagStorage,
  estLow: number,
  estHigh: number,
  outcome: LeadSubmitOutcome,
  nowMs: number = Date.now(),
): void {
  const payload: FlagPayload = { outcome, at: nowMs }
  storage.setItem(buildKey(estLow, estHigh), JSON.stringify(payload))
}

/**
 * Snapshot Object.keys for the supplied storage. Window's localStorage
 * doesn't expose a stable iterator, but Object.keys works.
 */
function getKeys(storage: FlagStorage): string[] {
  // Browser localStorage is window.localStorage which is also a
  // plain object on the read side. In jsdom and in our in-memory
  // stub, Object.keys works directly. We don't rely on `length` or
  // `key(i)` so we sidestep the differences between implementations.
  // The cast is safe because every implementation we care about
  // exposes own-properties matching the keys.
  return Object.keys(storage as unknown as Record<string, unknown>)
}

/**
 * Drop every `teaserLeadSubmitted:*` key. Called when starting a new
 * diagnostic attempt — the user's prior submissions are no longer
 * relevant to the fresh teaser.
 */
export function clearAllLeadFlags(storage: FlagStorage): void {
  for (const key of getKeys(storage)) {
    if (key.startsWith(FLAG_KEY_PREFIX)) {
      storage.removeItem(key)
    }
  }
}
